import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import UploadPanelVisual from '@components/framework/uploadpanel/UploadPanelVisual.tsx';
import { AttachmentsUploadedOutDTO } from '@api/ringil-api.v2.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

interface ComponentProps {
  onSave: (data: AttachmentsUploadedOutDTO[]) => void;
  /** Files collected before this panel was rendered — dropped or pasted onto whatever opened it. */
  pendingFiles?: File[];
}

const UploadPanel: React.FC<ComponentProps> = ({ onSave, pendingFiles }) => {
  const { uploadAttachments } = useContext(ApiContext);
  const [apiRequestCount, setApiRequestCount] = useState(0);
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const { setBeValidationErrors, clear: clearBeValidationErrors, BackendValidationComponent } = useBEValidation();
  const { showToastMessage } = useContext(ToastContext);
  const { tr } = useTranslationLgs();

  const processFiles = (selectedFiles: File[]) => {
    if (!selectedFiles) return;
    const files = selectedFiles.filter(file => {
      if (file.size <= MAX_FILE_SIZE_BYTES) return true;
      showToastMessage(
        tr('UploadPanel.fileTooLarge', '{{fileName}} is larger than 10 MB and was not uploaded', { fileName: file.name }),
        null,
        'warn',
      );
      return false;
    });
    // Nothing to upload: bail out before the in-flight counter goes up, or the panel would sit in
    // its loading state forever with no request to bring it back.
    if (files.length === 0) return;

    clearBeValidationErrors();
    const filesArray: { name: string; dataUrl: string }[] = [];

    setApiRequestCount(c => c + 1);
    files.forEach(file => {
      const reader = new FileReader();

      reader.onload = e => {
        const dataUrl = e.target?.result as string;
        filesArray.push({
          name: file.name,
          dataUrl,
        });

        // After processing all files, update the state
        if (filesArray.length === files.length) {
          uploadAttachments(
            { fileInDTOs: filesArray.map(file => ({ fileName: file.name, dataURL: file.dataUrl })) },
            d => {
              onSave(d);
              setApiRequestCount(c => c - 1);
            },
            null,
            {
              onError: (_error, showStandardError) => {
                setApiRequestCount(c => c - 1);
                showStandardError();
              },
              onValidationFailed: originalResponse => {
                setApiRequestCount(c => c - 1);
                setBeValidationErrors(originalResponse);
              },
            },
          );
        }
      };

      // A file that cannot be read would otherwise never reach the length check above and leave the
      // panel loading forever.
      reader.onerror = () => setApiRequestCount(c => c - 1);

      reader.readAsDataURL(file);
    });
  };

  const uploadedPendingFiles = useRef<File[]>(null);
  useEffect(() => {
    if (!pendingFiles?.length || uploadedPendingFiles.current === pendingFiles) return;
    uploadedPendingFiles.current = pendingFiles;
    processFiles(pendingFiles);
  }, [pendingFiles]);

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(Array.from(event.target.files ?? []));
    // Let the same file be picked again after it was removed from the list.
    event.target.value = '';
  };

  const handleClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  return (
    <>
      <BackendValidationComponent />
      <UploadPanelVisual
        onFiles={processFiles}
        onClick={() => {
          handleClick();
        }}
        showDropZone
        loading={apiRequestCount > 0}
      />
      <input type="file" ref={inputFileRef} onChange={handleFileSelection} className="hidden" multiple />
    </>
  );
};

export default UploadPanel;